<template>
  <div ref="rootEl" class="relative">
    <div class="flex flex-col gap-3 sm:flex-row">
      <button
        type="button"
        class="flex flex-1 items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors duration-150"
        :class="showDatePicker ? 'border-[#2D8CFF] bg-blue-50' : 'border-zinc-300 bg-white hover:bg-zinc-50'"
        @click="toggleDatePicker"
      >
        <CalendarDaysIcon class="h-6 w-6 text-zinc-500" />
        <span class="text-base" :class="date ? 'text-zinc-800' : 'text-zinc-400'">{{ dateLabel }}</span>
      </button>

      <button
        type="button"
        class="flex flex-1 items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors duration-150"
        :class="showTimePicker ? 'border-[#2D8CFF] bg-blue-50' : 'border-zinc-300 bg-white hover:bg-zinc-50'"
        @click="toggleTimePicker"
      >
        <ClockIcon class="h-6 w-6 text-zinc-500" />
        <span class="text-base" :class="time ? 'text-zinc-800' : 'text-zinc-400'">{{ timeLabel }}</span>
      </button>
    </div>

    <div
      v-if="showDatePicker"
      class="absolute left-0 z-50 mt-2 w-[260px] rounded-2xl border border-zinc-100 bg-white p-3 shadow-2xl"
    >
      <div class="mb-3 flex items-center justify-between">
        <button type="button" class="rounded-full p-1 hover:bg-zinc-100" @click="previousMonth">
          <ChevronLeftIcon class="h-4 w-4 text-zinc-600" />
        </button>
        <span class="text-base font-medium text-zinc-800">{{ monthLabel }}</span>
        <button type="button" class="rounded-full p-1 hover:bg-zinc-100" @click="nextMonth">
          <ChevronRightIcon class="h-4 w-4 text-zinc-600" />
        </button>
      </div>

      <div class="mb-1 grid grid-cols-7 text-center text-[11px] text-zinc-400">
        <span v-for="weekday in weekdayLabels" :key="weekday">{{ weekday }}</span>
      </div>
      <div class="grid grid-cols-7 gap-y-1 text-[13px]">
        <span v-for="blank in leadingBlanks" :key="`blank-${blank}`" />
        <button
          v-for="day in daysInMonth"
          :key="day"
          type="button"
          class="mx-auto flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-150"
          :class="dayClasses(day)"
          :disabled="isPastDay(day)"
          @click="selectDate(day)"
        >
          {{ day }}
        </button>
      </div>
    </div>

    <div
      v-if="showTimePicker"
      class="webinar-time-scroll absolute right-0 z-50 mt-2 max-h-80 w-[200px] overflow-y-auto rounded-2xl border border-zinc-100 bg-white py-3 shadow-2xl"
    >
      <button
        v-for="option in visibleTimes"
        :key="option.value"
        type="button"
        class="flex w-full items-center justify-between px-3 py-2 text-left text-[13px] font-medium text-zinc-800 transition-colors duration-150 hover:bg-zinc-100"
        @click="selectTime(option)"
      >
        <span>{{ option.label }}</span>
        <span v-if="time === option.value" class="text-[10px] font-semibold text-[#2D8CFF]">Selected</span>
      </button>
      <div v-if="!visibleTimes.length" class="px-3 py-3 text-xs leading-5 text-zinc-500">
        No future times remain for this date. Choose another day.
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
} from '@heroicons/vue/24/outline';

const props = defineProps({
  date: { type: String, default: '' },
  time: { type: String, default: '' },
  timezone: { type: String, default: 'America/New_York' },
});
const emit = defineEmits(['update:date', 'update:time']);

const rootEl = ref(null);
const showDatePicker = ref(false);
const showTimePicker = ref(false);
const nowTick = ref(Date.now());
let clockTimer = null;

const pad = (value) => String(value).padStart(2, '0');
const dateKey = (year, month, day) => `${year}-${pad(month + 1)}-${pad(day)}`;

function currentPartsInTimezone() {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: props.timezone || 'America/New_York',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date(nowTick.value));
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return {
      year: Number(values.year),
      month: Number(values.month) - 1,
      day: Number(values.day),
      hours: Number(values.hour),
      minutes: Number(values.minute),
    };
  } catch (_error) {
    const now = new Date(nowTick.value);
    return {
      year: now.getFullYear(), month: now.getMonth(), day: now.getDate(),
      hours: now.getHours(), minutes: now.getMinutes(),
    };
  }
}

const initialParts = currentPartsInTimezone();
const initialDateParts = String(props.date || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
const currentMonth = ref(initialDateParts ? Number(initialDateParts[2]) - 1 : initialParts.month);
const currentYear = ref(initialDateParts ? Number(initialDateParts[1]) : initialParts.year);
const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const monthLabel = computed(() => `${monthNames[currentMonth.value]} ${currentYear.value}`);
const daysInMonth = computed(() => new Date(currentYear.value, currentMonth.value + 1, 0).getDate());
const leadingBlanks = computed(() => new Date(currentYear.value, currentMonth.value, 1).getDay());
const todayKey = computed(() => {
  nowTick.value;
  const parts = currentPartsInTimezone();
  return dateKey(parts.year, parts.month, parts.day);
});

const dateLabel = computed(() => {
  const match = String(props.date || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return 'Date';
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
});

function timeLabelForValue(value) {
  const match = String(value || '').match(/^(\d{2}):(\d{2})$/);
  if (!match) return 'Time';
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return `${hours % 12 || 12}:${pad(minutes)} ${hours < 12 ? 'AM' : 'PM'}`;
}
const timeLabel = computed(() => timeLabelForValue(props.time));

const timeOptions = [];
for (let hours = 0; hours < 24; hours += 1) {
  for (let minutes = 0; minutes < 60; minutes += 5) {
    const value = `${pad(hours)}:${pad(minutes)}`;
    timeOptions.push({ value, hours, minutes, label: timeLabelForValue(value) });
  }
}

function isBlockedTimeForDate(option, selectedDate = props.date) {
  if (!selectedDate || selectedDate !== todayKey.value) return false;
  const now = currentPartsInTimezone();
  return (option.hours * 60) + option.minutes <= (now.hours * 60) + now.minutes;
}
const isBlockedTime = (option) => isBlockedTimeForDate(option, props.date);
const visibleTimes = computed(() => {
  nowTick.value;
  return timeOptions.filter((option) => !isBlockedTime(option));
});

function isPastDay(day) {
  return dateKey(currentYear.value, currentMonth.value, day) < todayKey.value;
}
function isSelectedDay(day) {
  return props.date === dateKey(currentYear.value, currentMonth.value, day);
}
function isToday(day) {
  return todayKey.value === dateKey(currentYear.value, currentMonth.value, day);
}
function dayClasses(day) {
  if (isPastDay(day)) return 'cursor-not-allowed text-zinc-300';
  if (isSelectedDay(day)) return 'border-none bg-[#2D8CFF] text-white hover:bg-[#1f7aeb]';
  return `${isToday(day) ? 'border border-zinc-300' : ''} cursor-pointer text-zinc-700 hover:bg-zinc-100`;
}

function previousMonth() {
  if (currentMonth.value === 0) {
    currentMonth.value = 11;
    currentYear.value -= 1;
  } else currentMonth.value -= 1;
}
function nextMonth() {
  if (currentMonth.value === 11) {
    currentMonth.value = 0;
    currentYear.value += 1;
  } else currentMonth.value += 1;
}
function selectDate(day) {
  if (isPastDay(day)) return;
  const nextDate = dateKey(currentYear.value, currentMonth.value, day);
  emit('update:date', nextDate);
  if (props.time) {
    const selected = timeOptions.find((option) => option.value === props.time);
    if (selected && isBlockedTimeForDate(selected, nextDate)) emit('update:time', '');
  }
  showDatePicker.value = false;
}
function selectTime(option) {
  if (isBlockedTime(option)) return;
  emit('update:time', option.value);
  showTimePicker.value = false;
}
function toggleDatePicker() {
  showDatePicker.value = !showDatePicker.value;
  if (showDatePicker.value) showTimePicker.value = false;
}
function toggleTimePicker() {
  showTimePicker.value = !showTimePicker.value;
  if (showTimePicker.value) showDatePicker.value = false;
}
function closeOnOutsideClick(event) {
  if (rootEl.value && !rootEl.value.contains(event.target)) {
    showDatePicker.value = false;
    showTimePicker.value = false;
  }
}

watch(
  () => [props.date, props.time, props.timezone, nowTick.value],
  () => {
    if (!props.date || !props.time) return;
    const selected = timeOptions.find((option) => option.value === props.time);
    if (selected && isBlockedTime(selected)) emit('update:time', '');
  }
);

onMounted(() => {
  document.addEventListener('click', closeOnOutsideClick);
  clockTimer = window.setInterval(() => { nowTick.value = Date.now(); }, 30000);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', closeOnOutsideClick);
  if (clockTimer) window.clearInterval(clockTimer);
});
</script>

<style scoped>
.webinar-time-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}
.webinar-time-scroll:hover {
  scrollbar-color: rgba(113, 113, 122, 0.45) transparent;
}
.webinar-time-scroll::-webkit-scrollbar {
  width: 6px;
}
.webinar-time-scroll::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
}
.webinar-time-scroll::-webkit-scrollbar-thumb {
  border-radius: 9999px;
  background: transparent;
}
.webinar-time-scroll:hover::-webkit-scrollbar-thumb {
  background: rgba(113, 113, 122, 0.45);
}
</style>
